<template>
  <div>
    <div v-if="loading" class="flex flex-col items-center justify-center py-24 text-slate-400">
      <div class="h-8 w-8 rounded-full border-2 border-slate-200 border-t-brand animate-spin"></div>
      <p class="mt-3 text-sm">Yükleniyor…</p>
    </div>

    <div v-else-if="notFound" class="text-center py-24">
      <div class="text-5xl mb-3">🔍</div>
      <p class="text-slate-500">Geçersiz veya süresi dolmuş bağlantı.</p>
    </div>

    <!-- Şifre ekranı -->
    <div v-else-if="!authed" class="max-w-sm mx-auto mt-16 rounded-3xl bg-white shadow-sm border border-slate-100 p-7">
      <div class="text-center">
        <div class="mx-auto h-14 w-14 rounded-2xl bg-brand/10 flex items-center justify-center text-2xl">🔒</div>
        <h1 class="text-lg font-bold text-slate-800 mt-4">Ajans Paneli</h1>
        <p class="text-sm text-slate-500 mt-1">Devam etmek için şifreni gir.</p>
      </div>
      <form @submit.prevent="doAuth" class="mt-6 space-y-3">
        <input
          v-model="password"
          type="password"
          placeholder="Şifre"
          autofocus
          class="w-full rounded-xl border border-slate-200 py-2.5 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
        />
        <button :disabled="!password || authBusy" type="submit" class="w-full rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-40 text-white font-semibold py-3 transition shadow-sm shadow-brand/30">
          {{ authBusy ? "Kontrol ediliyor…" : "Giriş" }}
        </button>
        <p v-if="authError" class="text-sm text-red-500 text-center">{{ authError }}</p>
      </form>
    </div>

    <template v-else>
      <!-- Başlık -->
      <div class="rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <div class="flex items-center gap-3">
          <span class="rounded-2xl bg-brand/10 flex items-center justify-center text-xl" style="width:44px;height:44px;flex:none;">🏢</span>
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium text-brand uppercase tracking-wide">{{ data.agencyName }}</p>
            <h1 class="text-lg font-bold text-slate-800 leading-tight">{{ data.campaignName }}</h1>
          </div>
        </div>
      </div>

      <!-- Brief -->
      <section v-if="data.briefText" class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Brief</p>
        <div class="brief-content text-slate-600 leading-relaxed" v-html="data.briefText"></div>
        <div v-if="data.publishDate" class="mt-5 rounded-2xl bg-brand/5 border border-brand/20 p-4 text-center">
          <p class="text-xs font-semibold text-brand uppercase tracking-wide">Yayınlanma Tarihi</p>
          <p class="text-xl font-extrabold text-slate-800 mt-1">{{ formatDate(data.publishDate) }}</p>
        </div>
      </section>

      <!-- Filtreler -->
      <section class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-5 sm:p-6">
        <input
          v-model="search"
          type="text"
          placeholder="Instagram ara…"
          class="w-full sm:max-w-xs rounded-xl border border-slate-200 py-2.5 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
        />
        <div class="mt-4 flex flex-wrap gap-2">
          <button
            @click="statusFilter = null"
            :class="['px-3 py-1.5 rounded-full text-xs font-semibold border transition', statusFilter === null ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-brand']"
          >Tümü ({{ scopedItems.length }})</button>
          <button
            v-for="s in statusOptions"
            :key="s"
            @click="statusFilter = s"
            :class="['px-3 py-1.5 rounded-full text-xs font-semibold border transition', statusFilter === s ? 'text-white ' + chipActiveBg(s) : 'bg-white text-slate-600 border-slate-200 hover:border-brand']"
          >{{ statusText(s) }} ({{ countByStatus(s) }})</button>
        </div>
      </section>

      <!-- Influencer tablosu -->
      <section class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 overflow-hidden">
        <div v-if="!filteredItems.length" class="text-center py-16 text-slate-400">
          Sonuç yok.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <th class="text-left font-semibold px-4 py-3 w-12">#</th>
                <th class="text-left font-semibold px-4 py-3">Instagram</th>
                <th class="text-left font-semibold px-4 py-3 w-36">Durum</th>
                <th class="text-left font-semibold px-4 py-3">Aksiyon</th>
                <th class="text-left font-semibold px-4 py-3 w-40">Yüklenenler</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in filteredItems" :key="item.id" class="border-t border-slate-100 align-top hover:bg-slate-50/60">
                <!-- sıra -->
                <td class="px-4 py-3 text-slate-400 tabular-nums">{{ i + 1 }}</td>

                <!-- instagram -->
                <td class="px-4 py-3">
                  <a v-if="isUrl(item.instagramHandle)" :href="item.instagramHandle" target="_blank" class="font-semibold text-slate-800 hover:text-brand">{{ igLabel(item.instagramHandle) }}</a>
                  <span v-else class="font-semibold text-slate-800">@{{ item.instagramHandle }}</span>
                </td>

                <!-- durum -->
                <td class="px-4 py-3">
                  <span :class="['inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full text-white', chipActiveBg(item.status)]">{{ statusText(item.status) }}</span>
                </td>

                <!-- aksiyon -->
                <td class="px-4 py-3">
                  <!-- İçerik yükle (Registered / Revision) -->
                  <div v-if="item.status === 0 || item.status === 2">
                    <div v-if="item.status === 2 && item.revisionNote" class="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
                      <span class="font-semibold text-amber-800">✏️ Revizyon:</span>
                      <span class="text-amber-700 whitespace-pre-line"> {{ item.revisionNote }}</span>
                    </div>
                    <div v-if="data.draftUrlLabel" class="mb-2">
                      <input v-model="draftUrls[item.id]" type="url" :placeholder="data.draftUrlLabel" class="w-full max-w-xs rounded-lg border border-slate-200 py-1.5 px-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
                    </div>
                    <button
                      :disabled="(!!data.draftUrlLabel && !draftUrls[item.id]) || busyId === item.id"
                      @click="pickFile(item, 0)"
                      class="rounded-lg bg-brand hover:bg-brand-dark disabled:opacity-40 text-white font-semibold py-1.5 px-3 text-xs transition whitespace-nowrap"
                    >{{ busyId === item.id ? "Yükleniyor…" : "İçerik yükle" }}</button>
                  </div>

                  <!-- İnceleniyor -->
                  <div v-else-if="item.status === 1" class="text-slate-500 text-xs">⏳ İnceleniyor</div>

                  <!-- Onaylandı → gönderi linki -->
                  <div v-else-if="item.status === 3">
                    <div class="flex flex-wrap items-center gap-2">
                      <input v-model="postUrls[item.id]" type="url" placeholder="Gönderi linki (https://…)" class="flex-1 min-w-[180px] max-w-xs rounded-lg border border-slate-200 py-1.5 px-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
                      <button
                        :disabled="!postUrls[item.id] || busyId === item.id"
                        @click="submitPostUrl(item)"
                        class="rounded-lg bg-brand hover:bg-brand-dark disabled:opacity-40 text-white font-semibold py-1.5 px-3 text-xs transition whitespace-nowrap"
                      >Gönder →</button>
                    </div>
                  </div>

                  <!-- Yayın → istatistik -->
                  <div v-else-if="item.status === 4">
                    <div v-if="countdownMs(item) > 0" class="text-violet-700 text-xs">
                      ⏱️ Kalan: <span class="font-extrabold tabular-nums">{{ countdownText(item) }}</span>
                    </div>
                    <div v-else>
                      <p class="text-amber-700 text-xs mb-1"><b>⚠️ Profil değil</b>, story istatistiği.</p>
                      <button
                        :disabled="busyId === item.id"
                        @click="pickFile(item, 2)"
                        class="rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white font-semibold py-1.5 px-3 text-xs transition whitespace-nowrap"
                      >{{ busyId === item.id ? "Yükleniyor…" : "Story istatistiği yükle" }}</button>
                    </div>
                  </div>

                  <!-- Tamamlandı -->
                  <div v-else-if="item.status === 5" class="text-emerald-600 font-medium text-xs">✅ Tamamlandı</div>
                </td>

                <!-- yüklenenler -->
                <td class="px-4 py-3">
                  <div v-if="item.submissions && item.submissions.length" class="flex flex-wrap gap-1.5">
                    <a v-for="s in item.submissions" :key="s.id" :href="s.fileUrl" target="_blank" class="relative">
                      <video v-if="isVideo(s.fileUrl)" :src="s.fileUrl" class="h-11 w-11 rounded-md object-cover border border-slate-200 bg-black" muted></video>
                      <img v-else :src="s.fileUrl" class="h-11 w-11 rounded-md object-cover border border-slate-200" />
                    </a>
                  </div>
                  <span v-else class="text-slate-300 text-xs">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- gizli dosya seçici -->
      <input ref="fileInput" type="file" class="hidden" :accept="pickAccept" :multiple="pickType === 2" @change="onFilePicked" />
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from "vue-property-decorator";
import api from "./api";

@Component
export default class AgencyPortal extends Vue {
  loading = true;
  notFound = false;
  authed = false;
  authBusy = false;
  authError = "";
  password = "";
  sessionToken = "";
  data: any = { influencers: [] };
  search = "";
  statusFilter: number | null = null;
  statusOptions = [0, 1, 2, 3, 4, 5];
  draftUrls: { [id: string]: string } = {};
  postUrls: { [id: string]: string } = {};
  busyId: string | null = null;
  now = Date.now();
  tick: any = null;
  refreshTimer: any = null;

  // Dosya seçimi için aktif hedef.
  pickTarget: any = null;
  pickType = 0;

  get token(): string {
    return this.$route.params.token;
  }

  get sessionKey(): string {
    return `agency-session-${this.token}`;
  }

  get pickAccept(): string {
    return this.pickType === 2 ? "image/*" : "image/*,video/*";
  }

  get items(): any[] {
    return this.data.influencers || [];
  }

  get scopedItems(): any[] {
    const q = (this.search || "").trim().toLowerCase();
    return this.items.filter((x) => !q || (x.instagramHandle || "").toLowerCase().includes(q));
  }

  get filteredItems(): any[] {
    return this.scopedItems.filter((x) => this.statusFilter === null || x.status === this.statusFilter);
  }

  countByStatus(s: number): number {
    return this.scopedItems.filter((x) => x.status === s).length;
  }

  async mounted() {
    const saved = sessionStorage.getItem(this.sessionKey);
    if (saved) {
      this.sessionToken = saved;
      await this.load();
    } else {
      this.loading = false;
    }
    this.tick = setInterval(() => (this.now = Date.now()), 1000);
    this.refreshTimer = setInterval(() => this.silentRefresh(), 15000);
  }

  beforeDestroy() {
    if (this.tick) clearInterval(this.tick);
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  async doAuth() {
    this.authBusy = true;
    this.authError = "";
    try {
      const res = await api.postJson(`/api/ContentCampaigns/agency/${this.token}/auth`, { password: this.password });
      this.sessionToken = res.sessionToken;
      sessionStorage.setItem(this.sessionKey, res.sessionToken);
      await this.load();
    } catch {
      this.authError = "Şifre hatalı.";
    } finally {
      this.authBusy = false;
    }
  }

  async load() {
    this.loading = true;
    try {
      this.data = await api.getJsonAuth(`/api/ContentCampaigns/agency/${this.token}`, this.sessionToken);
      this.seedInputs();
      this.authed = true;
    } catch (e: any) {
      if (e && e.message === "401") {
        // Oturum geçersiz; şifre ekranına dön.
        sessionStorage.removeItem(this.sessionKey);
        this.authed = false;
      } else {
        this.notFound = true;
      }
    } finally {
      this.loading = false;
    }
  }

  async silentRefresh() {
    if (!this.authed || this.busyId) return;
    try {
      this.data = await api.getJsonAuth(`/api/ContentCampaigns/agency/${this.token}`, this.sessionToken);
      this.seedInputs();
    } catch {
      /* sessiz geç */
    }
  }

  // Vue 2'de dinamik nesne anahtarları reaktif değildir; influencer başına alanları önceden tanımla.
  seedInputs() {
    for (const inf of this.items) {
      if (!(inf.id in this.draftUrls)) this.$set(this.draftUrls, inf.id, inf.draftUrl || "");
      if (!(inf.id in this.postUrls)) this.$set(this.postUrls, inf.id, "");
    }
  }

  pickFile(item: any, type: number) {
    this.pickTarget = item;
    this.pickType = type;
    (this.$refs.fileInput as HTMLInputElement).click();
  }

  async onFilePicked(e: any) {
    const files: File[] = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !this.pickTarget) return;
    const item = this.pickTarget;
    this.busyId = item.id;
    try {
      const form = new FormData();
      form.append("type", String(this.pickType));
      if (this.pickType === 0 && this.draftUrls[item.id]) form.append("draftUrl", this.draftUrls[item.id]);
      for (const f of files) form.append("file", f);
      await api.postFormAuth(`/api/ContentCampaigns/agency/${this.token}/influencers/${item.id}/submit`, form, this.sessionToken);
      await this.load();
    } catch {
      alert("Yükleme başarısız. Tekrar dene.");
    } finally {
      this.busyId = null;
    }
  }

  async submitPostUrl(item: any) {
    if (!this.postUrls[item.id]) return;
    this.busyId = item.id;
    try {
      const form = new FormData();
      form.append("type", "1");
      form.append("postUrl", this.postUrls[item.id]);
      await api.postFormAuth(`/api/ContentCampaigns/agency/${this.token}/influencers/${item.id}/submit`, form, this.sessionToken);
      this.postUrls[item.id] = "";
      await this.load();
    } catch {
      alert("Gönderilemedi. Tekrar dene.");
    } finally {
      this.busyId = null;
    }
  }

  countdownMs(item: any): number {
    if (!item.postSubmittedAt) return 0;
    const unlock = new Date(item.postSubmittedAt).getTime() + 24 * 60 * 60 * 1000;
    return Math.max(0, unlock - this.now);
  }

  countdownText(item: any): string {
    let s = Math.floor(this.countdownMs(item) / 1000);
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); s -= m * 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  statusText(s: number): string {
    return ["Kayıt", "İçerik Yüklendi", "Revizyon", "Onaylandı", "Yayınlandı", "İstatistik"][s] || "?";
  }

  chipActiveBg(s: number): string {
    return ["bg-slate-400 border-slate-400", "bg-blue-500 border-blue-500", "bg-orange-500 border-orange-500", "bg-emerald-500 border-emerald-500", "bg-violet-500 border-violet-500", "bg-teal-500 border-teal-500"][s] || "bg-slate-400";
  }

  isVideo(url: string): boolean {
    return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url || "");
  }
  isUrl(v: string): boolean {
    return /^https?:\/\//i.test(v || "");
  }
  igLabel(v: string): string {
    const m = /instagram\.com\/([A-Za-z0-9._]+)/i.exec(v || "");
    return m ? "@" + m[1] : v;
  }
  formatDate(value: string): string {
    if (!value) return "";
    return new Date(value).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  }
}
</script>

<style>
.brief-content { word-break: break-word; }
.brief-content h1 { font-size: 1.5rem; font-weight: 700; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content h2 { font-size: 1.25rem; font-weight: 700; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content p { margin: 0 0 0.6em; }
.brief-content ul { list-style: disc; padding-left: 1.4em; margin: 0 0 0.6em; }
.brief-content ol { list-style: decimal; padding-left: 1.4em; margin: 0 0 0.6em; }
.brief-content a { color: #6d28d9; text-decoration: underline; }
.brief-content strong { font-weight: 700; }
.brief-content u { text-decoration: underline; }
</style>
